import { useCallback, useEffect, useState } from 'react';
import type { DataTableColumn, DataTableStateChangeEvent } from '../DataTable.model';
import { DataTableEvent } from '../DataTable.model';
import { useDataTable } from '../DataTableProvider';

type ColumnMode = 'all' | 'leaf';

/**
 * Hook that returns reactive columns array that updates when columns change.
 * Subscribes to TableStateChange events to trigger re-renders when columns are added/removed/updated.
 *
 * @param mode - 'all' returns all columns including parents, 'leaf' returns renderable columns only
 * @returns Array of DataTable columns based on mode
 */
export const useColumns = (mode: ColumnMode = 'all'): ReadonlyArray<DataTableColumn> => {
  const { dataTableService } = useDataTable();
  const getColumns = useCallback(
    () => (mode === 'leaf' ? dataTableService.leafColumns : dataTableService.columns),
    [dataTableService, mode]
  );
  const [columns, setColumns] = useState<ReadonlyArray<DataTableColumn>>(getColumns);

  useEffect(() => {
    setColumns(getColumns());

    const abortController = new AbortController();

    const stateChangeListener = (event: DataTableStateChangeEvent) => {
      if (event.detail.type.has('columns')) {
        setColumns(getColumns());
      }
    };

    dataTableService.subscribe(DataTableEvent.TableStateChange, stateChangeListener, abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [dataTableService, mode, getColumns]);

  return columns;
};
